import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { portalAPI } from '@/lib/api/portal';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { CalendarDays, MapPin, Users } from 'lucide-react';
import { sanitizeHtml } from '@/lib/utils/sanitize';

export const Route = createFileRoute('/')({
  component: HomePage,
});

function HomePage() {
  const { data: event, isLoading, error } = useQuery({
    queryKey: ['event'],
    queryFn: () => portalAPI.getEvent(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading event details...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-destructive">Failed to load event details</div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="container py-8 space-y-8">
      {/* Hero Section */}
      <section className="text-center space-y-4">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground">
          {event.name}
        </h1>
        {event.shortDescription && (
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {event.shortDescription}
          </p>
        )}
      </section>

      {/* Event Info Cards */}
      <section className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <CalendarDays className="h-8 w-8 text-primary" />
            <div>
              <CardTitle className="text-lg">Date</CardTitle>
              <CardDescription>
                {formatDate(event.startDate)}
                {event.endDate !== event.startDate && (
                  <> - {formatDate(event.endDate)}</>
                )}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <MapPin className="h-8 w-8 text-primary" />
            <div>
              <CardTitle className="text-lg">Location</CardTitle>
              <CardDescription>
                {event.venue?.name || event.location || 'TBA'}
                {event.venue?.city && (
                  <>, {event.venue.city}, {event.venue.state}</>
                )}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center gap-4">
            <Users className="h-8 w-8 text-primary" />
            <div>
              <CardTitle className="text-lg">Registration</CardTitle>
              <CardDescription>
                {event.status === 'active' ? 'Open Now' :
                 event.status === 'published' ? 'Coming Soon' :
                 'Check Back Later'}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      </section>

      {/* CTA Section */}
      <section className="text-center py-8">
        <Link to="/register">
          <Button size="lg" className="text-lg px-8 py-6">
            Register Now
          </Button>
        </Link>
      </section>

      {/* Description */}
      {event.description && (
        <section className="prose prose-lg max-w-none">
          <Card>
            <CardHeader>
              <CardTitle>About This Event</CardTitle>
            </CardHeader>
            <CardContent>
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(event.description) }} />
            </CardContent>
          </Card>
        </section>
      )}
    </div>
  );
}
